import { useEffect, useState } from 'react';
import { ApolloError } from '@apollo/client';
import { message, Tooltip } from 'antd';

import { Button, Loading, QuickEdit } from '../../../../components';
import BorderedSection from '../../../../components/generic/BorderedSection';
import AntModal from '../../../../components/generic/AntModal';
import StandaloneForm from '../../../../components/generic/Form/StandaloneForm';
import { ModifyVariablesArgs } from '../../../../components/generic/Table/TableV3';
import QueryErrorBoundary from '../../../../components/error-handling';
import { useUserContext } from '../../../../context/PermissionsContext';
import { formatContactInfo } from '../../../../utils/formatUserData';

interface OwnersInformationProps {
  title: string;
  data: {
    uuid: string;
    sectionOwnerfirstName?: string;
    sectionOwnerlastName?: string;
    email?: string;
    sectionRequired?: boolean;
  };
  updateGQL: (variables: any) => Promise<any>;
  error?: ApolloError;
}

const OwnersInformation = ({
  title,
  data,
  updateGQL,
  error,
}: OwnersInformationProps) => {
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { isAdmin, canEdit } = useUserContext();

  const contactInfo = formatContactInfo({
    firstName: data?.sectionOwnerfirstName?.trim() || '',
    lastName: data?.sectionOwnerlastName?.trim() || '',
    email: data?.email?.trim() || '',
  });

  const fields = [
    {
      key: 'sectionOwnerfirstName',
      label: 'Section Owner First Name',
      type: 'text',
    },
    {
      key: 'sectionOwnerlastName',
      label: 'Section Owner Last Name',
      type: 'text',
    },
    {
      key: 'email',
      label: 'Email',
      type: 'email',
    },
    {
      key: 'sectionRequired',
      label: 'Section Required',
      type: 'select',
      options: [
        { value: 'true', label: 'Yes' },
        { value: 'false', label: 'No' },
      ],
    },
  ];

  const modifyVariables = ({ values }: ModifyVariablesArgs) => ({
    uuid: data.uuid,
    ...values,
    sectionRequired: values.sectionRequired === 'true',
  });

  const handleLoadingState = (state: boolean) => {
    setIsLoading(state);
  };

  useEffect(() => {
    if (isLoading) setOpenModal(false);
  }, [isLoading]);

  return (
    <>
      <BorderedSection title={title} noPadding>
        <QueryErrorBoundary error={error}>
          {!isLoading ? (
            <div className="flex flex-col xl:flex-row xl:justify-between xl:items-center p-5 gap-4 lg:gap-8 2xl:gap-14">
              <div className="flex items-center gap-6 flex-1">
                <label htmlFor="sectionOwner" className="text-primary-100">
                  Who owns this Section?
                </label>
                <Tooltip title={contactInfo}>
                  <input
                    id="sectionOwner"
                    className="rounded-md input-blueish-background border-[#CCCCCC] px-4 py-1.5 flex-1"
                    value={contactInfo || 'N/A'}
                    readOnly
                  />
                </Tooltip>
              </div>
              <div className="flex items-center gap-6 flex-1">
                <label htmlFor="sectionApplicable" className="text-primary-100">
                  Is this section applicable to this strategy?
                </label>
                <input
                  id="sectionApplicable"
                  className="rounded-md input-blueish-background border-[#CCCCCC] px-4 py-1.5 flex-1 text-center"
                  value={data?.sectionRequired ? 'Yes' : 'No'}
                  readOnly
                />
              </div>
              {(isAdmin || canEdit) && (
                <Button type="edit" onClick={() => setOpenModal(true)}>
                  <QuickEdit />
                </Button>
              )}
            </div>
          ) : (
            <Loading />
          )}
        </QueryErrorBoundary>
      </BorderedSection>
      <AntModal
        openModal={openModal}
        onCancel={() => setOpenModal(false)}
        title="Section Owner"
      >
        <StandaloneForm
          fields={fields}
          currentRecord={data}
          canDelete={false}
          updateGQL={updateGQL}
          modifyVariables={modifyVariables}
          setIsLoading={handleLoadingState}
          isSectionOwnerForm
        />
      </AntModal>
    </>
  );
};

export default OwnersInformation;
